import { defineStore } from 'pinia'

import { useConnectionStatus } from '@/composables/useConnectionStatus'
import GroupsService from '@/services/groups'
import type { GroupChatHead, GroupSummary } from '@/types/groups'

const UNREAD_POLL_MS = 15000

let unreadTimer: ReturnType<typeof setInterval> | null = null
let unreadVisibilityHandler: (() => void) | null = null
let unreadRefreshInFlight = false

export const useGroupChatsStore = defineStore('groupChats', {
  state: () => ({
    isOpen: false,
    groups: [] as GroupSummary[],
    loading: false,
    loaded: false,
    selectedGroupId: null as string | null,
    unreadByGroupId: {} as Record<string, number>,
    lastReadByGroupId: {} as Record<string, string | null>,
  }),

  getters: {
    activeGroups(state): GroupSummary[] {
      return state.groups
        .filter((group) => group.membershipStatus === 'active')
        .sort((a, b) => a.name.localeCompare(b.name, 'es'))
    },

    hasGroupChats(): boolean {
      return this.activeGroups.length > 0
    },

    selectedGroup(): GroupSummary | null {
      return this.activeGroups.find((group) => group.groupId === this.selectedGroupId) || null
    },

    totalUnread(state): number {
      return state.groups
        .filter((group) => group.membershipStatus === 'active')
        .reduce((sum, group) => sum + (state.unreadByGroupId[group.groupId] || 0), 0)
    },
  },

  actions: {
    async load() {
      this.loading = true
      try {
        const response = await GroupsService.listMine({ silent: true })
        this.groups = response.data || []
        const activeIds = new Set(this.activeGroups.map((group) => group.groupId))
        if (!this.selectedGroupId || !activeIds.has(this.selectedGroupId)) {
          this.selectedGroupId = this.activeGroups[0]?.groupId || null
        }
        this.pruneUnread(activeIds)
        if (activeIds.size === 0) {
          this.stopUnreadPolling()
          return
        }
        await this.refreshUnread()
        this.startUnreadPolling()
      } catch {
        this.groups = []
        this.selectedGroupId = null
        this.unreadByGroupId = {}
        this.lastReadByGroupId = {}
        this.stopUnreadPolling()
      } finally {
        this.loaded = true
        this.loading = false
      }
    },

    pruneUnread(activeIds: Set<string>) {
      const nextUnread: Record<string, number> = {}
      const nextRead: Record<string, string | null> = {}
      activeIds.forEach((groupId) => {
        if (groupId in this.unreadByGroupId) {
          nextUnread[groupId] = this.unreadByGroupId[groupId]
        }
        if (groupId in this.lastReadByGroupId) {
          nextRead[groupId] = this.lastReadByGroupId[groupId]
        }
      })
      this.unreadByGroupId = nextUnread
      this.lastReadByGroupId = nextRead
    },

    applyChatHead(groupId: string, head: GroupChatHead) {
      const currentRead = this.lastReadByGroupId[groupId]
      const incomingRead = head.lastReadMessageId
      if (currentRead && (!incomingRead || incomingRead < currentRead)) {
        return
      }
      this.lastReadByGroupId = {
        ...this.lastReadByGroupId,
        [groupId]: incomingRead,
      }
      this.unreadByGroupId = {
        ...this.unreadByGroupId,
        [groupId]: head.unreadCount,
      }
    },

    async refreshUnread() {
      if (unreadRefreshInFlight) return
      if (!useConnectionStatus().isOnline.value) return
      const groupIds = this.activeGroups.map((group) => group.groupId)
      if (groupIds.length === 0) return
      unreadRefreshInFlight = true
      try {
        await Promise.all(groupIds.map(async (groupId) => {
          try {
            const response = await GroupsService.getChatHead(groupId)
            this.applyChatHead(groupId, response.data)
          } catch {
            // Keep the previous count when one group fails.
          }
        }))
      } finally {
        unreadRefreshInFlight = false
      }
    },

    startUnreadPolling() {
      if (unreadTimer) return
      unreadTimer = setInterval(() => {
        if (document.hidden) return
        if (!useConnectionStatus().isOnline.value) return
        void this.refreshUnread()
      }, UNREAD_POLL_MS)
      unreadVisibilityHandler = () => {
        if (!document.hidden) {
          void this.refreshUnread()
        }
      }
      document.addEventListener('visibilitychange', unreadVisibilityHandler)
    },

    stopUnreadPolling() {
      if (unreadTimer) {
        clearInterval(unreadTimer)
        unreadTimer = null
      }
      if (unreadVisibilityHandler) {
        document.removeEventListener('visibilitychange', unreadVisibilityHandler)
        unreadVisibilityHandler = null
      }
    },

    open() {
      this.isOpen = true
      if (!this.selectedGroupId && this.activeGroups[0]) {
        this.selectedGroupId = this.activeGroups[0].groupId
      }
    },

    close() {
      this.isOpen = false
    },

    reset() {
      this.stopUnreadPolling()
      this.close()
      this.groups = []
      this.loaded = false
      this.selectedGroupId = null
      this.unreadByGroupId = {}
      this.lastReadByGroupId = {}
    },

    selectGroup(groupId: string) {
      this.selectedGroupId = groupId
    },
  },
})
